import { useRef, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Fade,
  Link,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import everguardLogo from "../assets/EverGuard_Logo.svg"
import { useAuth } from '../context/AuthContext';
import axios from "axios";

const LoginPage = () => {
  const { login } = useAuth();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(["", "", "", ""]);
  
  const inputRefs = useRef([]);
  const [step, setStep] = useState('phone');
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (step === 'otp' && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [step]);

  const handlePhoneChange = (e) => {
    // Keep only digits
    let value = e.target.value.replace(/\D/g, "");

    value = value.slice(0, 10);

    // Add space after first 5 digits
    if (value.length > 5) {
      value = `${value.slice(0, 5)} ${value.slice(5)}`;
    }

    setPhone(value);
  };

  const sendOTP = async () => {
    setLoading(true);
    setAlert(null);

    try {
      const formattedPhone = phone.replace(/\s/g, "");

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}api/v1/send-otp/`,
        {
          phone_number: formattedPhone,
        }
      );

      if (response.data.success) {
        return true;
      }

      setAlert({
        type: "error",
        message: response.data.message || "Failed to send OTP.",
      });

      return false;
    } catch (error) {
      console.error("Send OTP Error:", error);

      setAlert({
        type: "error",
        message:
          error.response?.data?.message ||
          "Something went wrong. Please try again.",
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();

    const success = await sendOTP();

    if (success) {
      setStep("otp");
    }
  };

  const handleResendOTP = async () => {
    setOtp(["", "", "", ""]);

    const success = await sendOTP();

    if (success) {
      setAlert({
        type: "success",
        message: "A new OTP has been sent.",
      });
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    setLoading(true);
    setAlert(null);

    const enteredOtp = otp.join("");
    const formattedPhone = phone.replace(/\s/g, "");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}api/v1/login/`,
        {
          phone_number: formattedPhone,
          otp: enteredOtp,
        }
      );

      if (response.data.success) {
        login({
          role: "posp",
          ...response.data.data,
        });
      } else {
        setAlert({
          type: "error",
          message: response.data.message || "Invalid OTP. Please try again.",
        });
      }
    } catch (error) {
      console.error("Verify OTP Error:", error);
      
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Invalid OTP. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value, index) => {

    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);

    const newOtp = [...otp];

    pasted.split("").forEach((digit, i) => {
      newOtp[i] = digit;
    });

    setOtp(newOtp);

    inputRefs.current[Math.min(pasted.length, 4)]?.focus();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d1b6e 0%, #1a237e 40%, #00695c 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >      
      {[...Array(5)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.08)',            
            width: `${200 + i * 120}px`,
            height: `${200 + i * 120}px`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        />
      ))}

      <Fade in timeout={600}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper
            elevation={24}
          sx={{
            width: { xs: '90%', sm: 430 },
            p: { xs: 3, sm: 5 },
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.97)',
            boxShadow: '2px 2px 8px 4px rgba(14, 8, 8, 0.7)',
            backdropFilter: 'blur(20px)',
            position: 'relative',
            zIndex: 1,
          }}
        >          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
            <Box sx={{  pl: 1, pr: 2, borderRadius: 2,}}>
              <img
                src={everguardLogo}
                alt="EverGuard"
                style={{
                  height: 70,
                  width: 310,
                  objectFit: 'contain',
                  objectPosition: 'left',
                  transition: 'all 0.25s ease',
                }}
              />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.5, mt:4 }}>
              POSP Training Portal
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {alert && (
            <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 2, borderRadius: 2, }}>
              {alert.message}
            </Alert>
          )}

          {step === 'otp' && (
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
              <IconButton onClick={() => {
                  setStep("phone");
                  setPhone("");
                  setOtp(["", "", "", ""]);
                  setAlert(null);
                }}>
                <ArrowBackIcon />
              </IconButton>

              <Box>
                <Typography variant="h6" sx={{ lineHeight: 1.4 }}>
                  Verify Account
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ lineHeight: 1.4, display: "block" }}
                >
                  Enter the OTP sent to +91 ***** {phone.slice(6,11)}
                </Typography>
              </Box>
            </Box>
          )}

          <form onSubmit={step === 'phone' ? handleSendOTP : handleVerifyOTP}>
            {step === 'phone' ? (
            <Box sx={{ display: "flex", mb: 3 }}>

              {/* Static Country Code */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  border: "1px solid",
                  borderColor: "grey.400",
                  borderRight: "none",
                  borderRadius: "4px 0 0 4px",
                  bgcolor: "grey.100",
                  minWidth: 70,
                }}
              >
                <Typography fontWeight={500}>+91</Typography>
              </Box>

              {/* Phone Number Field */}
              <TextField
                fullWidth
                label="Phone Number"
                variant="outlined"
                value={phone}
                type="tel"
                onChange={handlePhoneChange}
                inputProps={{
                  maxLength: 11, // 10 digits + 1 space
                  inputMode: "numeric",
                  pattern: "[0-9 ]*",
                }}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0 4px 4px 0",
                  },
                  "& fieldset": {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                }}
              />
            </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1.5,
                  mb: 3,
                }}
                onPaste={handlePaste}
              >
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    value={digit}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    onChange={(e) => handleChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    inputProps={{
                      maxLength: 1,
                      inputMode: "numeric",            
                    }}
                    sx={{
                      width: 55,
                      "& input": {
                        textAlign: "center",
                        fontSize: "1.4rem",
                        fontWeight: 600,
                        padding: 0,
                      },
                      "& .MuiOutlinedInput-root": {
                        height: 55,
                        borderRadius: 2,
                      },
                    }}
                  />
                ))}
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #1a237e, #00897b)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #000051, #005b4f)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(26,35,126,0.4)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? 'Processing...' : step === 'phone' ? 'Get OTP' : 'Verify and Login'}
            </Button>
            {step === `otp` && (
              <Box sx={{mt: 2, justifyContent: 'center', display: 'flex'}}>
              <Typography 
              onClick={handleResendOTP}
              variant="body2" sx={{textDecoration: 'underline', cursor: 'pointer', fontWeight: 700}} color="primary">Resend Code</Typography>
              </Box>
            )}
          </form>
        </Paper>
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }} align="center">
              Developed by{" "}
              <Link
                href="https://keelis.com/"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="inherit"
              >
                Keelis
              </Link>
            </Typography>
          </Box>
        </Box>
      </Fade>          
    </Box>
  );
};

export default LoginPage;
